import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import userSwipeMachineService from "../../services/SwipeMachine-UserAccess/UserSwipeMachineService";
import "./userSwipeMachineDialog.scss";
import DeliveryCashService from "../../services/deliverycash/deliverycashservice";

interface UserOption {
  userID: number;
  name: string;
  primaryContactNbr: string;
  displayText: string;
}

export default function UserSwipeMachineDialog({
  visible,
  onHide,
  editData,
  onSaved,
  showToast
}: any) {
  const toast = useRef<Toast>(null);
  const [machines, setMachines] = useState<any[]>([]);
  const [userId, setUserId] = useState<any>(null);
  const [swipeMachineId, setSwipeMachineId] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList: UserOption[] = await DeliveryCashService.GetuserlistSearch();

        const mappedUsers = userList.map((user) => ({
          ...user,
          displayText: `${user.name} - ${user.primaryContactNbr}`,
        }));

        setUsers(mappedUsers);
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load users.",
        });
      }
    };

    const fetchMachines = async () => {
      const r = await userSwipeMachineService.getSwipeMachines();

      const mapped = (r?.data ?? []).map((m: any) => ({
        id: m.swipeMachineId,
        name: `${m.serialNo}`
      }));

      setMachines(mapped);
    };

    fetchMachines();

    fetchUsers();
  }, []);

  const selectedUserTemplate = (option: UserOption) => {
    if (option) {
      return (
        <div className="flex flex-col">
          {option.name} - {option.primaryContactNbr}
        </div>
      );
    }
    return <span>Select User</span>;
  };

  const userOptionTemplate = (option: UserOption) => {
    return (
      <div className="flex flex-col">
        {option.name} - {option.primaryContactNbr}
      </div>
    );
  };

  useEffect(() => {
    if (!visible || !editData || users.length === 0) return;
    const matchedUser =
      users.find(u => u.userID === editData.userId) || null;

    setSelectedUser(matchedUser);
    setUserId(editData.userId);
    setSwipeMachineId(editData.swipeMachineId);
    setIsEditMode(true);

  }, [visible, editData, users]);

  useEffect(() => {
    if (!visible) return;
    if (!editData) {
      setSelectedUser(null);
      setUserId(null);
      setSwipeMachineId(null);
      setIsEditMode(false);
    }
  }, [visible, editData]);


  const save = async () => {
    if (!userId || !swipeMachineId) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "User and Swipe Machine are required"
      });
      return;
    }

    setSaving(true);

    try {
      let response;

      if (editData) {
        const payload = {
          assignedId: editData.id,
          swipeMachineId: swipeMachineId,
          isDelete: false, // editing does not delete        
        };

        response = await userSwipeMachineService.updateUserSwipeMachineMapping(payload);

      } else {
        // ➕ ADD MODE → ADD API
        const payload = {
          userId: userId,
          swipeMachineId: swipeMachineId,
          isDelete: false
        };

        response = await userSwipeMachineService.addUserSwipeMachineMapping(payload);

      }

      if (response?.isSuccess === true) {
        showToast({
          severity: "success",
          summary: "Success",
          detail: response.message
        });
      }

      if (response?.isSuccess === false) {
        toast.current?.show({
          severity: "warn",
          summary: "Warning",
          detail: response.message
        });
        return;
      }

      toast.current?.show({
        severity: "success",
        summary: "Success",
          detail: response.message
      });

      onSaved();
      onHide();

    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save access"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      header={editData ? "Edit Swipe Machine Access" : "Add Swipe Machine Access"}
      visible={visible}
      onHide={onHide}
      modal
      className="swipe-machine-dialog"
      style={{ width: '90vw', maxWidth: '500px' }}
      footer={
        <div className="dialog-buttons">
          <Button label="Cancel" severity="secondary" onClick={onHide} />
          <Button
            label="Save"
            loading={saving}
            className="p-button-success"
            onClick={save}
          />
        </div>
      }
    >
      <Toast ref={toast} />

      <form className="swipe-machine-form">
        <div className="field">
          <label>
            User <span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={selectedUser}
            onChange={(e: DropdownChangeEvent) => {
              setSelectedUser(e.value);
              setUserId(e.value?.userID ?? null);
            }}
            options={users}
            optionLabel="displayText"
            placeholder="Select User"
            filter
            filterBy="displayText"
            filterMatchMode="contains"
            valueTemplate={selectedUserTemplate}
            itemTemplate={userOptionTemplate}
            className="w-full"
            disabled={isEditMode}
          />
        </div>

        <div className="field">
          <label>
            Swipe Machine #<span className="text-red-500">*</span>
          </label>
          <Dropdown
            value={swipeMachineId}
            options={machines}
            optionLabel="name"
            optionValue="id"
            onChange={(e) => setSwipeMachineId(e.value)}
            className="w-full"
            placeholder="Select Swipe Machine"
          />
        </div>
      </form>
    </Dialog>
  );
}
